import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import './QuoteSearchBar.css';

const QuoteSearchBar = ({ quotes, onQuoteSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef();
  const dropdownRef = useRef();

  // Filter quotes based on search term
  const filteredQuotes = React.useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const searchLower = searchTerm.toLowerCase();
    return quotes
      .filter(quote => 
        (quote.project_name && quote.project_name.toLowerCase().includes(searchLower)) ||
        (quote.company_name && quote.company_name.toLowerCase().includes(searchLower)) ||
        (quote.client_name && quote.client_name.toLowerCase().includes(searchLower))
      )
      .slice(0, 8); // Limit to 8 results
  }, [quotes, searchTerm]);

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedIndex(-1);
    setIsOpen(value.trim().length > 0);
  };

  // Handle quote selection
  const handleQuoteSelect = (quote) => {
    setSearchTerm('');
    setIsOpen(false);
    setSelectedIndex(-1);
    onQuoteSelect(quote);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || filteredQuotes.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredQuotes.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : filteredQuotes.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleQuoteSelect(filteredQuotes[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
    setIsOpen(false);
    setSelectedIndex(-1);
    searchRef.current?.focus();
  };

  return (
    <div className="quote-search-container" ref={dropdownRef}>
      <div className="quote-search-input-wrapper">
        <Search size={18} className="search-icon" />
        <input
          ref={searchRef}
          type="text"
          placeholder="Search quotes by project or client name..."
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="quote-search-input"
        />
        {searchTerm && (
          <button onClick={clearSearch} className="clear-search-btn">
            <X size={16} />
          </button>
        )}
      </div>

      {isOpen && filteredQuotes.length > 0 && (
        <div className="quote-search-dropdown">
          {filteredQuotes.map((quote, index) => (
            <div
              key={quote.id}
              className={`quote-search-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleQuoteSelect(quote)}
            >
              <div className="quote-item-main">
                <div className="quote-item-title">
                  {quote.project_name || 'Unnamed Project'}
                </div>
                <div className="quote-item-client">
                  {quote.company_name || quote.client_name || 'No Client'}
                </div>
              </div>
              <div className="quote-item-status">
                <span className={`status-badge status-${quote.quote_status?.toLowerCase().replace(' ', '-')}`}>
                  {quote.quote_status || 'No Status'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {isOpen && searchTerm.trim() && filteredQuotes.length === 0 && (
        <div className="quote-search-dropdown">
          <div className="quote-search-no-results">
            No quotes found matching "{searchTerm}"
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteSearchBar;